/**
 * Type guards and validation utilities for LLM result parsing.
 *
 * Since LLM responses can vary in structure or fail parsing,
 * these utilities provide runtime safety and meaningful fallbacks.
 */

import { logger } from '../lib/logger';
import { LLMConfidenceLevel } from '../types';
import { SCORE_THRESHOLDS } from '../constants';

// ============================================================================
// TYPE GUARD FUNCTIONS
// ============================================================================

/**
 * Checks if a value is a non-null object
 */
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Checks if a value is a valid string array
 */
function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

/**
 * Checks if a value is a valid confidence value (number 0-1 or 'High'/'Medium'/'Low')
 */
function isValidConfidence(value: unknown): value is LLMConfidenceLevel | number {
  if (typeof value === 'number') {
    return value >= 0 && value <= 1;
  }
  if (typeof value === 'string') {
    return ['High', 'Medium', 'Low'].includes(value);
  }
  return false;
}

/**
 * Checks if a value is a valid score (number 0-100)
 */
function isValidScore(value: unknown): value is number {
  return typeof value === 'number' && value >= 0 && value <= 100;
}

/**
 * Type guard for breakdown item
 */
interface BreakdownItem {
  category: string;
  score: number;
  reason: string;
}

function isValidBreakdownItem(item: unknown): item is BreakdownItem {
  if (!isObject(item)) return false;
  return (
    typeof item.category === 'string' &&
    typeof item.score === 'number' &&
    typeof item.reason === 'string'
  );
}

/**
 * Type guard for authenticity analysis breakdown record
 */
interface AuthenticityBreakdownRecord {
  score: number;
  reason: string;
}

function isValidAuthenticityBreakdownRecord(
  value: unknown
): value is Record<string, AuthenticityBreakdownRecord> {
  if (!isObject(value)) return false;
  return Object.values(value).every(
    item => isObject(item) && typeof item.score === 'number' && typeof item.reason === 'string'
  );
}

/**
 * Type guard for authenticity analysis
 */
interface AuthenticityAnalysis {
  overallScore?: number;
  summary?: string;
  breakdown?: Record<string, { score: number; reason: string }>;
  flags?: string[];
}

function isValidAuthenticityAnalysis(value: unknown): value is AuthenticityAnalysis {
  if (!isObject(value)) return false;

  // Check optional fields have correct types if present
  if ('overallScore' in value && value.overallScore !== undefined) {
    if (!isValidScore(value.overallScore)) return false;
  }
  if ('summary' in value && value.summary !== undefined) {
    if (typeof value.summary !== 'string') return false;
  }
  if ('breakdown' in value && value.breakdown !== undefined) {
    if (!isValidAuthenticityBreakdownRecord(value.breakdown)) return false;
  }
  if ('flags' in value && value.flags !== undefined) {
    if (!isStringArray(value.flags)) return false;
  }

  return true;
}

/**
 * Type guard for extracted data
 */
interface ExtractedData {
  patientName?: string;
  providerName?: string;
  issueDate?: string;
  expirationDate?: string;
  animalType?: string;
  breed?: string;
  weight?: string;
  microchipNumber?: string;
  rabiesVaccineDate?: string;
  additionalNotes?: string;
}

function isValidExtractedData(value: unknown): value is ExtractedData {
  if (!isObject(value)) return false;

  // All fields are optional strings
  const stringFields = [
    'patientName', 'providerName', 'issueDate', 'expirationDate',
    'animalType', 'breed', 'weight', 'microchipNumber',
    'rabiesVaccineDate', 'additionalNotes'
  ];

  for (const field of stringFields) {
    if (field in value && value[field] !== undefined && value[field] !== null) {
      if (typeof value[field] !== 'string') return false;
    }
  }

  return true;
}

// ============================================================================
// VALIDATION RESULT TYPES
// ============================================================================

export interface LLMResultValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ValidatedLLMResult {
  /** Whether the document passed verification */
  isValid: boolean;
  /** Confidence level (0-1 or 'High'/'Medium'/'Low') */
  confidence: LLMConfidenceLevel | number | null;
  /** AI reasoning for the decision */
  reasoning: string | null;
  /** Summary of the analysis */
  summary: string | null;
  /** Final score 0-100 */
  finalScore: number | null;
  /** Provider name from document */
  providerName: string | null;
  /** License number from document */
  licenseNumber: string | null;
  /** Red flags found */
  redFlags: string[];
  /** Pet name from document */
  petName: string | null;
  /** Pet breed */
  breed: string | null;
  /** Pet type (dog, cat, etc) */
  petType: string | null;
  /** Document date */
  documentDate: string | null;
  /** Expiry date */
  expiryDate: string | null;
  /** List of vaccinations */
  vaccinations: string[];
  /** Veterinarian name */
  veterinarianName: string | null;
  /** Clinic name */
  clinicName: string | null;
  /** Raw extracted text */
  extractedText: string | null;
  /** Authenticity analysis breakdown */
  authenticityAnalysis: AuthenticityAnalysis | null;
  /** Score breakdown items */
  breakdown: BreakdownItem[];
  /** Extracted structured data */
  extractedData: ExtractedData;
  /** Microchip number */
  microchipNumber: string | null;
  /** Rabies vaccine date */
  rabiesVaccineDate: string | null;
  /** Additional notes */
  additionalNotes: string | null;
  /** Whether this was successfully validated */
  _wasValidated: boolean;
  /** Validation errors if any */
  _validationErrors: string[];
}

// ============================================================================
// MAIN VALIDATION FUNCTION
// ============================================================================

/**
 * Validates an LLM result and returns a safe, validated version.
 *
 * This function:
 * 1. Checks if the data matches expected structure
 * 2. Provides safe defaults for missing/invalid fields
 * 3. Logs validation failures for debugging
 *
 * @param data - Raw LLM result from database (could be anything)
 * @param uploadId - Upload ID for logging context
 * @returns Validated result with safe defaults
 */
export function validateLLMResult(
  data: unknown,
  uploadId?: string
): ValidatedLLMResult {
  const errors: string[] = [];
  const context = uploadId ? `[Upload: ${uploadId.slice(0, 8)}]` : '';

  // Handle null/undefined input
  if (data === null || data === undefined) {
    return createEmptyValidatedResult(false, ['LLM result is null or undefined']);
  }

  // Must be an object
  if (!isObject(data)) {
    const error = `LLM result is not an object (got ${typeof data})`;
    errors.push(error);
    logger.warn(`${context} LLM validation failed: ${error}`);
    return createEmptyValidatedResult(false, errors);
  }

  const raw = data as Record<string, unknown>;

  // Validate and extract each field with safe defaults
  const result: ValidatedLLMResult = {
    isValid: typeof raw.isValid === 'boolean' ? raw.isValid : false,
    confidence: isValidConfidence(raw.confidence) ? raw.confidence : null,
    reasoning: typeof raw.reasoning === 'string' ? raw.reasoning : null,
    summary: typeof raw.summary === 'string' ? raw.summary : null,
    finalScore: isValidScore(raw.finalScore) ? raw.finalScore : null,
    providerName: typeof raw.provider_name === 'string' ? raw.provider_name : null,
    licenseNumber: typeof raw.license_number === 'string' ? raw.license_number : null,
    redFlags: isStringArray(raw.red_flags) ? raw.red_flags : [],
    petName: typeof raw.petName === 'string' ? raw.petName : null,
    breed: typeof raw.breed === 'string' ? raw.breed : null,
    petType: typeof raw.petType === 'string' ? raw.petType : null,
    documentDate: typeof raw.documentDate === 'string' ? raw.documentDate : null,
    expiryDate: typeof raw.expiryDate === 'string' ? raw.expiryDate : null,
    vaccinations: isStringArray(raw.vaccinations) ? raw.vaccinations : [],
    veterinarianName: typeof raw.veterinarianName === 'string' ? raw.veterinarianName : null,
    clinicName: typeof raw.clinicName === 'string' ? raw.clinicName : null,
    extractedText: typeof raw.extractedText === 'string' ? raw.extractedText : null,
    authenticityAnalysis: isValidAuthenticityAnalysis(raw.authenticityAnalysis)
      ? raw.authenticityAnalysis as AuthenticityAnalysis
      : null,
    breakdown: [],
    extractedData: isValidExtractedData(raw.extractedData)
      ? raw.extractedData as ExtractedData
      : {},
    microchipNumber: typeof raw.microchipNumber === 'string' ? raw.microchipNumber : null,
    rabiesVaccineDate: typeof raw.rabiesVaccineDate === 'string' ? raw.rabiesVaccineDate : null,
    additionalNotes: typeof raw.additionalNotes === 'string' ? raw.additionalNotes : null,
    _wasValidated: true,
    _validationErrors: errors,
  };

  // Validate breakdown array
  if (Array.isArray(raw.breakdown)) {
    result.breakdown = raw.breakdown.filter(isValidBreakdownItem);
    const invalidCount = raw.breakdown.length - result.breakdown.length;
    if (invalidCount > 0) {
      errors.push(`${invalidCount} invalid breakdown items were filtered out`);
    }
  }

  // Log any validation issues
  if (errors.length > 0) {
    logger.debug(`${context} LLM result validation had ${errors.length} issues`, { errors });
  }

  return result;
}

/**
 * Creates an empty validated result with error information.
 * Exported so components can use it as a fallback when no LLM data exists.
 */
export function createEmptyValidatedResult(wasValidated: boolean = false, errors: string[] = []): ValidatedLLMResult {
  return {
    isValid: false,
    confidence: null,
    reasoning: null,
    summary: null,
    finalScore: null,
    providerName: null,
    licenseNumber: null,
    redFlags: [],
    petName: null,
    breed: null,
    petType: null,
    documentDate: null,
    expiryDate: null,
    vaccinations: [],
    veterinarianName: null,
    clinicName: null,
    extractedText: null,
    authenticityAnalysis: null,
    breakdown: [],
    extractedData: {},
    microchipNumber: null,
    rabiesVaccineDate: null,
    additionalNotes: null,
    _wasValidated: wasValidated,
    _validationErrors: errors,
  };
}

// ============================================================================
// HELPER FUNCTIONS FOR COMPONENTS
// ============================================================================

/**
 * Checks if an LLM result has authenticity analysis data
 */
export function hasAuthenticityAnalysis(result: ValidatedLLMResult): boolean {
  return result.finalScore !== null || result.authenticityAnalysis !== null;
}

/**
 * Gets the overall score from either finalScore or authenticityAnalysis
 */
export function getOverallScore(result: ValidatedLLMResult): number | null {
  return result.finalScore ?? result.authenticityAnalysis?.overallScore ?? null;
}

/**
 * Gets the summary from either summary or authenticityAnalysis
 */
export function getSummary(result: ValidatedLLMResult): string | null {
  return result.summary ?? result.authenticityAnalysis?.summary ?? null;
}

/**
 * Gets all flags/red flags from the result
 */
export function getFlags(result: ValidatedLLMResult): string[] {
  const flags = [...result.redFlags];
  if (result.authenticityAnalysis?.flags) {
    flags.push(...result.authenticityAnalysis.flags);
  }
  return flags;
}

/**
 * Gets the breakdown items, converting from either array or object format
 */
export function getBreakdownItems(result: ValidatedLLMResult): BreakdownItem[] {
  // If we have a direct breakdown array, use it
  if (result.breakdown.length > 0) {
    return result.breakdown;
  }

  // Otherwise try to convert from authenticityAnalysis.breakdown
  if (result.authenticityAnalysis?.breakdown) {
    return Object.entries(result.authenticityAnalysis.breakdown).map(([key, val]) => ({
      category: key.replace(/([A-Z])/g, ' $1').trim(),
      score: val.score,
      reason: val.reason,
    }));
  }

  return [];
}

/**
 * Formats a confidence value for display
 */
export function formatConfidence(confidence: LLMConfidenceLevel | number | null): string {
  if (confidence === null) return 'Unknown';
  if (typeof confidence === 'number') {
    return `${(confidence * 100).toFixed(0)}%`;
  }
  return confidence;
}

/**
 * Single source of truth for mapping a numeric score to a semantic status.
 * Every UI branch that cares about "verified vs flagged vs rejected" calls this.
 */
export function getScoreStatus(score: number): 'verified' | 'flagged' | 'rejected' {
  if (score >= SCORE_THRESHOLDS.VERIFIED) return 'verified';
  if (score >= SCORE_THRESHOLDS.REVIEW) return 'flagged';
  return 'rejected';
}

/**
 * Determines the score color class based on threshold
 */
export function getScoreColorClass(score: number | null, isDark: boolean = false): string {
  if (score === null) return isDark ? 'text-gray-400' : 'text-gray-500';
  if (score >= SCORE_THRESHOLDS.VERIFIED) return isDark ? 'text-green-400' : 'text-green-600';
  if (score >= SCORE_THRESHOLDS.REVIEW) return isDark ? 'text-yellow-400' : 'text-yellow-600';
  return isDark ? 'text-red-400' : 'text-red-600';
}

/**
 * Determines the score background class based on threshold
 */
export function getScoreBgClass(score: number | null, isDark: boolean = false): string {
  if (score === null) return isDark ? 'bg-gray-900/30' : 'bg-gray-50';
  if (score >= SCORE_THRESHOLDS.VERIFIED) return isDark ? 'bg-green-900/10' : 'bg-green-50';
  if (score >= SCORE_THRESHOLDS.REVIEW) return isDark ? 'bg-yellow-900/10' : 'bg-yellow-50';
  return isDark ? 'bg-red-900/10' : 'bg-red-50';
}

/**
 * Determines the score border class based on threshold
 */
export function getScoreBorderClass(score: number | null, isDark: boolean = false): string {
  if (score === null) return isDark ? 'border-gray-700' : 'border-gray-200';
  if (score >= SCORE_THRESHOLDS.VERIFIED) return isDark ? 'border-green-900/30' : 'border-green-100';
  if (score >= SCORE_THRESHOLDS.REVIEW) return isDark ? 'border-yellow-900/30' : 'border-yellow-100';
  return isDark ? 'border-red-900/30' : 'border-red-100';
}
